import * as React from 'react';
import { createStyles, Text, Alert, Accordion } from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { IconAlertCircle } from '@tabler/icons-react';

// Components
import { PrimaryText } from '../PrimaryText';

// Hooks
import { useLatestNote } from '../../hooks/useLatestNote';

// Types
import { ISurveyFull, ISurveyNoteType } from '../../types';

const useStyles = createStyles((theme) => ({
    note: {
        whiteSpace: 'pre'
    },
    accordionContent: {
        // padding: 0
    },
    accordionControl: {
        borderRadius: 0
    },
    accordionItem: {
        border: `0.0625rem solid ${theme.colors.gray[3]}`
    },
    noBorder: {
        border: 'none'
    }
}));

interface LatestReviewNoteProps {
    survey: ISurveyFull;
    displayStatuses?: ISurveyNoteType[];
    noBorder?: boolean;
}

export const LatestReviewNote: React.FC<LatestReviewNoteProps> = ({ survey, noBorder, displayStatuses = [] }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const latestNote = useLatestNote(survey);
    const color = React.useMemo(() => {
        if (!latestNote) return '';
        switch (latestNote.type) {
            case 'APPROVE':
                return 'blue';
            case 'REJECT':
                return 'red';
            default:
                return 'gray';
        }
    }, [latestNote]);

    return displayStatuses.includes(latestNote?.type) ? (
        <Accordion
            variant={'default'}
            defaultValue="main"
            classNames={{
                content: classes.accordionContent,
                item: classes.accordionItem,
                control: classes.accordionControl
            }}
        >
            <Accordion.Item
                value={'main'}
                className={cx('', {
                    [classes.noBorder]: noBorder
                })}
            >
                <Accordion.Control>
                    <Text size="xs" c="dimmed">
                        {t('latestReviewNote', { ns: 'common' })}:
                    </Text>
                </Accordion.Control>
                <Accordion.Panel>
                    <Alert
                        color={color}
                        title={t(`latestReviewNote.type.${latestNote.type}`, { ns: 'components' })}
                        icon={<IconAlertCircle size="1rem" />}
                    >
                        <Text>{latestNote?.note}</Text>
                        <Text mt="xl" color="red" fw={500}>
                            <Trans
                                t={t}
                                i18nKey={'latestReviewNote.guidance'}
                                ns="components"
                                components={{
                                    highlight: <PrimaryText />
                                }}
                            />
                        </Text>
                    </Alert>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    ) : null;
};
